import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import SessionContext from '../../../library/Core/SessionContext';

type CampusState = {
    instituteId: number | null;
    setInstituteId: (id: number | null) => void;
    countsVersion: number;
    refreshCounts: () => void;
};

const CampusContext = createContext<CampusState | null>(null);

type CampusSession = {
    userTypeCode?: string;
    role?: string;
    institutes?: unknown;
    primaryInstitute?: unknown;
} | null | undefined;

function isManagementLogin(sessionState: CampusSession) {
    const codes = [sessionState?.userTypeCode, sessionState?.role];
    return codes.includes('INSTITUTE_ADMIN') || codes.includes('SCHOOL_ADMIN');
}

function listedInstitutes(sessionState: CampusSession) {
    const raw = sessionState?.institutes;
    if (!Array.isArray(raw)) return [];
    return raw.filter((id) => id != null && id !== '');
}

function assignedIds(sessionState: CampusSession) {
    const listed = listedInstitutes(sessionState);
    if (listed.length) return listed.map((id) => Number(id));
    const primary = sessionState?.primaryInstitute;
    return primary != null && primary !== '' ? [Number(primary)] : [];
}

function startingInstitute(sessionState: CampusSession) {
    if (!isManagementLogin(sessionState)) return null;
    const listed = listedInstitutes(sessionState);
    if (listed.length === 1) return Number(listed[0]);
    if (listed.length > 1) return null;
    const primary = sessionState?.primaryInstitute;
    return primary != null && primary !== '' ? Number(primary) : null;
}

export function CampusProvider({ children }: { children: ReactNode }) {
    const { sessionState } = useContext(SessionContext);
    const [instituteId, setInstituteId] = useState<number | null>(() => startingInstitute(sessionState));
    const seeded = useRef(false);

    useEffect(() => {
        if (!isManagementLogin(sessionState) || seeded.current) return;
        const listed = listedInstitutes(sessionState);
        if (listed.length === 0 && !sessionState?.primaryInstitute) return;
        seeded.current = true;
        const ids = assignedIds(sessionState);
        setInstituteId(ids.length === 1 ? ids[0] : null);
    }, [sessionState]);
    const [countsVersion, setCountsVersion] = useState(0);
    const refreshCounts = useCallback(() => setCountsVersion((version) => version + 1), []);
    const value = useMemo(
        () => ({ instituteId, setInstituteId, countsVersion, refreshCounts }),
        [instituteId, countsVersion, refreshCounts]
    );
    return <CampusContext.Provider value={value}>{children}</CampusContext.Provider>;
}

export function useCampus() {
    const value = useContext(CampusContext);
    if (!value) throw new Error('Campus selection is only available inside Custom Reports');
    return value;
}
